import { ISourceDataEvent, ISourceDataForm } from "@/api/dtos";
import { useScopedField } from "@ngt/forms-core";
import { useMemo } from "react";
import { ISourceDataVerificationEvent, SourceDataType, ISourceDataVerificationSection, ISourceDataVerificationField, ISourceDataVerificationForm } from '../../utilities/forms/SourceDataFormProcessor';
import { ISourceDataField } from '../../api/dtos';

export interface ISourceDataReviewCount {
    reviewed: number;
    verified: number;
    totalToReview: number;
    totalToVerify: number;
    total: number;
}

export interface ISourceDataReviewCounts{
    identifier: Record<string, ISourceDataReviewCount>;
    review: ISourceDataReviewCount;
}

const useSourceDataReviewCounts = (eventInfos: ISourceDataVerificationEvent[]) => {
    const { state: { value }} = useScopedField<Record<string, Record<number, ISourceDataEvent>>>('events', { value: true }, false, true);

    const counts = useMemo(() => {
        const result: ISourceDataReviewCounts = {
            identifier: {
                
            },
            review: {
                reviewed: 0,
                verified: 0,
                totalToReview: 0,
                totalToVerify: 0,
                total: 0
            }
        };

        if (!eventInfos) {
            return {
                identifier: {
                    
                },
                review: {
                    reviewed: 0,
                    verified: 0,
                    totalToReview: 0,
                    totalToVerify: 0,
                    total: 0
                }
            }
        }

        for (let eventInfo of eventInfos ?? []) {
            const eventCounts = processEventCounts(result, eventInfo, value?.[eventInfo.eventKey!]?.[eventInfo.eventRepeat ?? 1]);

            result.review.total += eventCounts.total;
            result.review.reviewed += eventCounts.reviewed;
            result.review.verified += eventCounts.verified;
            result.review.totalToReview += eventCounts.totalToReview;
            result.review.totalToVerify += eventCounts.totalToVerify;
        }



        return result;
    }, [value, eventInfos])

    return counts;
}

export const processReviewCounts = (eventInfos: ISourceDataVerificationEvent[], events?: Record<string, Record<number, ISourceDataEvent>>) => {
    const result: ISourceDataReviewCounts = {
        identifier: {
            
        },
        review: {
            reviewed: 0,
            verified: 0,
            totalToReview: 0,
            totalToVerify: 0,
            total: 0
        }
    };

    if (!eventInfos) {
        return {
            identifier: {
                
            },
            review: {
                reviewed: 0,
                verified: 0,
                totalToReview: 0,
                totalToVerify: 0,
                total: 0
            }
        }
    }

    for (let eventInfo of eventInfos ?? []) {
        const eventCounts = processEventCounts(result, eventInfo, events?.[eventInfo.eventKey!]?.[eventInfo.eventRepeat ?? 1]);

        result.review.total += eventCounts.total;
        result.review.reviewed += eventCounts.reviewed;
        result.review.verified += eventCounts.verified;
        result.review.totalToReview += eventCounts.totalToReview;
        result.review.totalToVerify += eventCounts.totalToVerify;
    }



    return result;
}

const processEventCounts = (counts: ISourceDataReviewCounts, eventInfo: ISourceDataVerificationEvent, event?: ISourceDataEvent) => {
    let total = 0;
    let reviewed = 0;
    let verified = 0;
    let totalToReview = 0;
    let totalToVerify = 0;

    if (eventInfo.eventRepeat) {
        for (let formInfo of eventInfo.forms ?? []) {
            const formCounts = processFormCounts(counts, formInfo, event?.forms?.[formInfo.formKey!]);

            total += formCounts.total;
            reviewed += formCounts.reviewed;
            verified += formCounts.verified;
            totalToReview += formCounts.totalToReview;
            totalToVerify += formCounts.totalToVerify;
        }
    }

    counts.identifier[eventInfo.eventIdentifier!] = {
        reviewed,
        verified,
        totalToReview,
        totalToVerify,
        total
    };

    return counts.identifier[eventInfo.eventIdentifier!];
}

const processFormCounts = (counts: ISourceDataReviewCounts, formInfo: ISourceDataVerificationForm, form?: ISourceDataForm) => {
    
    let total = 0;
    let reviewed = 0;
    let verified = 0;
    let totalToReview = 0;
    let totalToVerify = 0;

    for (let field of formInfo.fields ?? []) {
        if (field.type === SourceDataType.Section) {
            const sectionsCounts = {
                reviewed: 0,
                verified: 0,
                totalToReview: 0,
                totalToVerify: 0,
                total: 0
            }

            for (let sectionField of field.fields ?? []) {
                if (sectionField.formSectionRepeat) {
                    const fieldCounts = processFieldCount(counts, sectionField, form?.fields?.[sectionField.formFieldKey!]?.[sectionField.formSectionRepeat]);

                    sectionsCounts.total += fieldCounts.total;
                    sectionsCounts.reviewed += fieldCounts.reviewed;
                    sectionsCounts.verified += fieldCounts.verified;
                    sectionsCounts.totalToReview += fieldCounts.totalToReview;
                    sectionsCounts.totalToVerify += fieldCounts.totalToVerify;
                }
            }

            counts.identifier[field.formSectionIdentifier!]

            total += sectionsCounts.total;
            reviewed += sectionsCounts.reviewed;
            verified += sectionsCounts.verified;
            totalToReview += sectionsCounts.totalToReview;
            totalToVerify += sectionsCounts.totalToVerify;
        }
        else {
            const fieldCounts = processFieldCount(counts, field, form?.fields?.[field.formFieldKey!]?.[1]);

            total += fieldCounts.total;
            reviewed += fieldCounts.reviewed;
            verified += fieldCounts.verified;
            totalToReview += fieldCounts.totalToReview;
            totalToVerify += fieldCounts.totalToVerify;
        }
    }

    counts.identifier[formInfo.formIdentifier!] = {
        reviewed,
        verified,
        totalToReview,
        totalToVerify,
        total
    };

    return counts.identifier[formInfo.formIdentifier!];
}

const processFieldCount = (counts: ISourceDataReviewCounts, fieldInfo: ISourceDataVerificationField, field?: ISourceDataField) => {

    let total = 0;
    let reviewed = 0;
    let verified = 0;
    let totalToReview = 0;
    let totalToVerify = 0;

    total++;

    if (fieldInfo?.formFieldConfiguration?.allowSourceDataVerification && fieldInfo?.formFieldConfiguration?.allowReview) {
        totalToReview++;

        if (field?.reviewed) {
            reviewed++;
        }
    }

    if (fieldInfo?.formFieldConfiguration?.allowSourceDataVerification && fieldInfo?.formFieldConfiguration?.allowVerify) {
        totalToVerify++;

        if (field?.verified) {
            verified++;
        }
    }

    counts.identifier[fieldInfo.formFieldIdentifier!] = {
        reviewed,
        verified,
        totalToReview,
        totalToVerify,
        total
    };

    return counts.identifier[fieldInfo.formFieldIdentifier!];
}

export default useSourceDataReviewCounts;